import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { logging } from 'helpers/monitoring';
import { EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { UserService } from 'shared/services';

@Injectable()
export class GlobalUnauthorizedInterceptor implements HttpInterceptor {
    constructor(
        private authService: AuthService,
        private userService: UserService
    ) {}

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(req).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.status !== 401) {
                    return throwError(() => error);
                }

                return this.handleBackendUnauthorizedError(error).pipe(
                    switchMap((shouldLogout) => {
                        if (shouldLogout) {
                            this.userService.removeUser();
                            this.authService.loginWithRedirect({
                                appState: { target: window.location.origin },
                                authorizationParams: {
                                    screen_hint: 'login'
                                }
                            });
                            return EMPTY;
                        }
                        return throwError(() => error);
                    })
                );
            })
        );
    }

    private handleBackendUnauthorizedError(error: HttpErrorResponse): Observable<boolean> {
        logging.warn(`GlobalUnauthorizedInterceptor: Detected 401 from ${error.url}. Checking token validity.`, null);

        return this.authService.getAccessTokenSilently({ cacheMode: 'cache-only' }).pipe(
            switchMap((accessToken) => {
                if (!accessToken) {
                    logging.warn('getAccessTokenSilently returned no token. Performing logout.', null);
                    this.userService.removeUser();
                    this.authService.loginWithRedirect();
                    return of(true);
                }

                const payload = this.decodeAccessTokenPayload(accessToken);

                if (!payload) {
                    logging.warn('Access token payload could not be decoded. Performing logout.', null);
                    return of(true);
                }

                if (typeof payload.exp === 'number') {
                    const nowInSeconds = Date.now() / 1000;
                    if (payload.exp < nowInSeconds) {
                        logging.warn('Access token from getAccessTokenSilently is expired. Performing logout.', null);
                        return of(true);
                    } else {
                        if (error.error && error.error.message.includes('token')) {
                            return of(true);
                        }
                        logging.warn(
                            'Access token from getAccessTokenSilently is not expired. Backend returned 401 for other not token-related reasons.',
                            null
                        );
                        return of(false);
                    }
                } else {
                    logging.warn(
                        'Cannot determine access token expiry (no exp claim or invalid format in payload). Performing logout.',
                        null
                    );
                    return of(true);
                }
            }),
            catchError((err) => {
                logging.error('Error calling getAccessTokenSilently or token not in cache. Performing logout.', err);
                return of(true);
            })
        );
    }

    private decodeAccessTokenPayload(accessToken: string): any | null {
        try {
            const tokenParts = accessToken.split('.');
            if (tokenParts.length !== 3) {
                logging.warn('Invalid access token format: not enough parts. Cannot decode payload.', null);
                return null;
            }

            const payloadBase64Url = tokenParts[1];
            const payloadBase64 = payloadBase64Url.replace(/-/g, '+').replace(/_/g, '/');
            const decodedPayload = atob(payloadBase64);
            return JSON.parse(decodedPayload);
        } catch (error) {
            logging.error('Error decoding access token payload.', error);
            return null;
        }
    }
}
